import {useAddNotification, useTransition} from '@yandex-delivery/blocks'
import {useController, useFormContext} from '@yandex-delivery/form'
import {useDebounce} from '@yandex-delivery/hooks'
import {isEqual, omit, raise} from '@yandex-delivery/utils'
import React, {memo, useMemo, useRef, useState} from 'react'

import {fetchClosestOfferDeprecated} from 'src/shared/api'
import {fromOrderToNddClaim, fromOrderToNddClaimMobile} from 'src/shared/api/deprecated/utils/from-ndd-order'
import {appConfig, i18n} from 'src/shared/config'
import {NDD_PACKAGE_SIZES} from 'src/shared/consts'
import type {NddClaim, Order} from 'src/shared/types'

import {EstimateDeliverySchema, EstimateDeliverySchemaMobile} from './EstimateDelivery.schema'

type Props = {
  name: string
  body: Partial<NddClaim>
}

export type EstimateDeliveryWidgetState = {
  status: 'pending' | 'fulfilled' | 'rejected'
  id?: string
  price?: string
  totalPrice?: string
}

const EMPTY_OFFER_ERROR_MESSAGE = 'empty offer'
const INVALID_SENDER_PHONE_CODE = 'invalid_sender_phone'

const Widget = memo<Props>(function Widget({name, body}) {
  const {control} = useFormContext()
  const {field} = useController({name, control})
  const addNotification = useAddNotification()

  useTransition(
    function _fetchClosestOfferDeprecated() {
      return Promise.resolve()
        .then(() => {
          field.onChange({
            status: 'pending',
            id: undefined,
            price: undefined,
            totalPrice: undefined,
          })
        })
        .then(() =>
          fetchClosestOfferDeprecated(body)
            .then(closestOffer => {
              if (closestOffer === undefined) {
                // Дальше не мучаем форму установкой значений. По выбранным адресам не существует
                // предложений по доставке в указанный интервал
                raise(EMPTY_OFFER_ERROR_MESSAGE)
              }

              field.onChange({
                status: 'fulfilled',
                id: closestOffer.offer_id,
                // @ts-expect-error<TS2339> Property 'offer_details' does not exist on type 'unknown'
                price: closestOffer.offer_details?.pricing,
                // @ts-expect-error<TS2339> Property 'offer_details' does not exist on type 'unknown'
                totalPrice: closestOffer.offer_details?.pricing_total,
              })
            })
            .catch(error => {
              if (error.message === EMPTY_OFFER_ERROR_MESSAGE) {
                addNotification(
                  i18n(
                    'ndd.offers.create.error.empty_valid_offers',
                    'К сожалению, доставка в указанный интервал невозможна',
                  ),
                  {type: 'error'},
                )
              }

              if (error?.body?.message && error?.body?.code === INVALID_SENDER_PHONE_CODE) {
                addNotification(error.body.message, {type: 'error'})
              }

              field.onChange({
                status: 'fulfilled',
                id: undefined,
                price: undefined,
                totalPrice: undefined,
              })
            }),
        )
    },
    {
      key: `fetch/closest-offer-${name}`,
    },
  )

  return null
}, isEqual)

type ObservableOrderValues = Omit<Order, 'nddOrder'>

// Сейчас время 22:00 по Москве, поэтому я ничего не нашел лучше чем ТАКОЕ костыльное решение!
// Завтра у нас запуск проекта и нужно показать рабочий прототип
// FIXME - найти более лучшее решение потомкам (тикет в котором подобная работа делалась https://nda.ya.ru/t/wgyxKbbK74sUqw)
// FIXME - завел тикет на рефакторинг этого куска кода (https://nda.ya.ru/t/9h8TQYkl74sUqT)
export const EstimateDeliveryWidget = memo(function EstimateDeliveryWidget() {
  const {values} = useFormContext<Order>()
  const [validValue, setValidValue] = useState<string>()
  const prevMaybeValues = useRef<string | undefined>(undefined)

  // Откидываем nddOrder чтобы не зациклить себя в обновлении значений. Почему так сделано?! Смотри комментарий выше
  const formValuesWithoutNddOrder: ObservableOrderValues = useMemo(() => {
    return omit('nddOrder', values)
  }, [values])

  const {body, key} = useMemo(() => {
    if (validValue === undefined) {
      prevMaybeValues.current = undefined

      return {
        body: undefined,
        key: undefined,
      }
    }

    if (prevMaybeValues.current === validValue) {
      return {
        body: undefined,
        key: undefined,
      }
    }

    prevMaybeValues.current = validValue

    return {
      body:
        appConfig.device === 'desktop'
          ? fromOrderToNddClaim(JSON.parse(validValue), NDD_PACKAGE_SIZES)
          : fromOrderToNddClaimMobile(JSON.parse(validValue), NDD_PACKAGE_SIZES),
      key: validValue,
    }
  }, [validValue])

  // В день запуска обнаружилось, что валидация работает на каждый чих изменения формы
  // Ничего быстрее я не нашел кроме как вкорячить debounce. Поэтому так. Коммент и ссылка на рефакторинг выше
  useDebounce(
    () => {
      try {
        setValidValue(
          JSON.stringify(
            appConfig.device === 'desktop'
              ? EstimateDeliverySchema.validateSync(formValuesWithoutNddOrder)
              : EstimateDeliverySchemaMobile.validateSync(formValuesWithoutNddOrder),
          ),
        )
      } catch {
        setValidValue(undefined)
      }
    },
    300,
    [formValuesWithoutNddOrder],
  )

  return body ? <Widget key={key} body={body} name="nddOrder" /> : null
})
